// ---------------------------------------------------------------------------
// Spaced repetition service: every graded attempt updates the question's SM-2
// state, and the Dashboard / Infinite Practice surface what's due today.
// ---------------------------------------------------------------------------

import type { InterviewQuestion } from '../types';
import * as repo from '../db/repository';
import { nowIso } from '../utils/id';
import { SM2_INITIAL, dueDate, qualityFromScore, sm2Update } from './sm2';

/** Record a graded attempt into the review schedule. */
export function recordReview(questionId: string, finalScore: number, now: Date = new Date()): void {
  const existing = repo.getReviewState(questionId);
  const prev = existing
    ? { ease: existing.ease, intervalDays: existing.intervalDays, repetitions: existing.repetitions }
    : SM2_INITIAL;
  const next = sm2Update(prev, qualityFromScore(finalScore));
  repo.upsertReviewState({
    questionId,
    ease: next.ease,
    intervalDays: next.intervalDays,
    repetitions: next.repetitions,
    dueAt: dueDate(next.intervalDays, now).toISOString(),
    lastScore: finalScore,
    updatedAt: nowIso(),
  });
}

/** Questions due for review right now (oldest due first). */
export function listDueQuestions(): InterviewQuestion[] {
  return repo.listDueQuestions();
}

export function countDueQuestions(): number {
  return repo.countDueQuestions();
}

/** Push a question's next review out by `days` without touching SM-2 state. */
export function snoozeReview(questionId: string, days = 3, now: Date = new Date()): void {
  const existing = repo.getReviewState(questionId);
  if (!existing) return;
  repo.upsertReviewState({
    ...existing,
    dueAt: dueDate(days, now).toISOString(),
    updatedAt: nowIso(),
  });
}

/** Pull a question back into today's due queue. */
export function reviewSooner(questionId: string, now: Date = new Date()): void {
  const existing = repo.getReviewState(questionId) ?? {
    questionId,
    ...SM2_INITIAL,
    dueAt: now.toISOString(),
    lastScore: 0,
    updatedAt: nowIso(),
  };
  repo.upsertReviewState({ ...existing, dueAt: now.toISOString(), updatedAt: nowIso() });
}

/**
 * Mark a question mastered: schedule it a year out with a high ease so it
 * effectively leaves the rotation (it can always be pulled back with
 * "review sooner").
 */
export function markMastered(questionId: string, now: Date = new Date()): void {
  const existing = repo.getReviewState(questionId);
  repo.upsertReviewState({
    questionId,
    ease: Math.max(existing?.ease ?? 2.5, 2.5),
    intervalDays: 365,
    repetitions: Math.max(existing?.repetitions ?? 0, 5),
    dueAt: dueDate(365, now).toISOString(),
    lastScore: existing?.lastScore ?? 100,
    updatedAt: nowIso(),
  });
}

/** Human explanation of why/when a question is scheduled — for review chips. */
export function describeReviewState(state: {
  dueAt: string;
  intervalDays: number;
  repetitions: number;
  ease: number;
  lastScore: number;
}): string {
  const due = new Date(state.dueAt);
  const days = Math.ceil((due.getTime() - Date.now()) / 86_400_000);
  const when = days <= 0 ? 'due now' : days === 1 ? 'due tomorrow' : `due in ${days} days`;
  return (
    `${when} · interval ${Math.round(state.intervalDays)}d · ` +
    `${state.repetitions} successful review${state.repetitions === 1 ? '' : 's'} · ` +
    `ease ${state.ease.toFixed(2)} · last score ${Math.round(state.lastScore)}`
  );
}
